import { AuditLogPage, AuditLogQueryParams, AuditResourceType } from '_lib/api';
import apiRoute from '_lib/api/paths';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AuditFilters,
  AuditListState,
  AuditState,
  DEFAULT_AUDIT_ITEMS_PER_PAGE,
  HISTORY_LIMIT,
  historyKey,
  initialAuditFilters,
  newAuditList,
} from './auditTypes';

export interface HistoryQuery {
  resourceType: AuditResourceType;
  resourceId: string;
  limit?: number;
}

export interface GlobalResponsePayload {
  queryKey: string;
  envelope: AuditLogPage;
}

export interface GlobalFailurePayload {
  queryKey: string;
  error: string;
}

// `key` identifies the slot in `state.history`, `queryKey` guards against a stale response
// for that same slot overwriting a newer one.
export interface HistoryResponsePayload extends GlobalResponsePayload {
  key: string;
}

export interface HistoryFailurePayload extends GlobalFailurePayload {
  key: string;
}

export const historyQueryParams = (query: HistoryQuery): AuditLogQueryParams => ({
  resourceType: query.resourceType,
  resourceId: query.resourceId,
  page: 1,
  limit: query.limit ?? HISTORY_LIMIT,
});

const initialState: AuditState = {
  list: newAuditList(),
  filters: initialAuditFilters,
  paginationCurrentPage: 1,
  paginationItemsPerPage: DEFAULT_AUDIT_ITEMS_PER_PAGE,
  history: {},
};

// Parameter is named `state` on purpose: no-param-reassign only whitelists that name
const applyResponse = (state: AuditListState, envelope: AuditLogPage) => {
  state.loading = false;
  state.error = null;
  state.results = envelope?.results ?? [];
  state.total = envelope?.total ?? 0;
  state.fetchedAt = Date.now();
};

const auditReducer = createSlice({
  name: 'audit',
  initialState,
  reducers: {
    /*
     * `auditLogsRequest` and `historyLogsRequest` are also keys of `caseSagas` in ./sagas.ts,
     * so a single dispatch both flips `loading` here and starts the fetch. Same deliberate
     * namespace collision `denms/denmsRequest` relies on — do not rename one side alone.
     */
    auditLogsRequest: (state, action: PayloadAction<AuditLogQueryParams>) => {
      state.list.loading = true;
      state.list.error = null;
      state.list.queryKey = apiRoute.auditLogs(action.payload);
    },
    historyLogsRequest: (state, action: PayloadAction<HistoryQuery>) => {
      const key = historyKey(action.payload.resourceType, action.payload.resourceId);
      if (!state.history[key]) {
        state.history[key] = newAuditList();
      }
      const slot = state.history[key];
      // keep any results already loaded so Refresh does not blank the list
      slot.loading = true;
      slot.error = null;
      slot.queryKey = apiRoute.auditLogs(historyQueryParams(action.payload));
    },

    auditLogsResponse: (state, action: PayloadAction<GlobalResponsePayload>) => {
      if (state.list.queryKey !== action.payload.queryKey) {
        return;
      }
      applyResponse(state.list, action.payload.envelope);
    },
    auditLogsFailed: (state, action: PayloadAction<GlobalFailurePayload>) => {
      if (state.list.queryKey !== action.payload.queryKey) {
        return;
      }
      state.list.loading = false;
      state.list.error = action.payload.error;
      state.list.fetchedAt = Date.now();
    },

    historyLogsResponse: (state, action: PayloadAction<HistoryResponsePayload>) => {
      const slot = state.history[action.payload.key];
      if (!slot || slot.queryKey !== action.payload.queryKey) {
        return;
      }
      applyResponse(slot, action.payload.envelope);
    },
    historyLogsFailed: (state, action: PayloadAction<HistoryFailurePayload>) => {
      const slot = state.history[action.payload.key];
      if (!slot || slot.queryKey !== action.payload.queryKey) {
        return;
      }
      slot.loading = false;
      slot.error = action.payload.error;
      slot.fetchedAt = Date.now();
    },

    setFilters: (state, action: PayloadAction<Partial<AuditFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // any filter change can shrink the result set below the current page
      state.paginationCurrentPage = 1;
    },
    resetFilters: (state) => {
      state.filters = initialAuditFilters;
      state.paginationCurrentPage = 1;
    },
    setPaginationCurrentPage: (state, action: PayloadAction<number>) => {
      state.paginationCurrentPage = action.payload;
    },
    setPaginationItemsPerPage: (state, action: PayloadAction<number>) => {
      state.paginationItemsPerPage = action.payload;
      state.paginationCurrentPage = 1;
    },
  },
});

export default auditReducer;
